import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Heart, Wind, Star, MessageCircle, BookOpen, HelpCircle, Users, Home, Menu, X, LogOut, User } from "lucide-react";

const navItems = [
  { to: "/", label: "Home", icon: Home },
  { to: "/check-in", label: "Check-in", icon: Heart },
  { to: "/calm", label: "Calm Tools", icon: Wind },
  { to: "/affirmations", label: "Affirmations", icon: Star },
  { to: "/talk", label: "Talk to Sovha", icon: MessageCircle },
  { to: "/journal", label: "My Journal", icon: BookOpen },
  { to: "/understand", label: "Understand", icon: HelpCircle },
  { to: "/support", label: "For Someone", icon: Users },
];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { user, logout, promptAuth } = useAuth();

  const handleQuickExit = () => {
    window.location.replace("https://www.google.com");
  };

  return (
    <div className="min-h-screen bg-earth-50 font-body" data-testid="app-layout">
      {/* Top Nav */}
      <nav className="sticky top-0 z-40 bg-earth-50/90 backdrop-blur-xl border-b border-earth-200/60" data-testid="main-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
              <img src="/sovha-logo.png" alt="Sovha" className="h-12" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden lg:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                      active
                        ? "bg-terracotta-light text-terracotta"
                        : "text-earth-600 hover:text-earth-800 hover:bg-earth-200/50"
                    }`}
                  >
                    <Icon size={16} />
                    <span>{item.label}</span>
                  </Link>
                );
              })}
            </div>

            {/* Account icon + Mobile menu */}
            <div className="flex items-center gap-2">
              {user ? (
                <div className="hidden lg:flex items-center gap-3 mr-2">
                  <span className="text-earth-500 text-sm">{user.name}</span>
                  <button onClick={logout} data-testid="logout-btn" className="text-earth-400 hover:text-earth-600 transition-colors" title="Sign out">
                    <LogOut size={16} />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => promptAuth("Create a free account")}
                  data-testid="account-icon-btn"
                  className="hidden lg:flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium text-earth-500 hover:text-earth-800 hover:bg-earth-200/50 transition-all duration-300"
                >
                  <User size={16} />
                  <span>Account</span>
                </button>
              )}
              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="lg:hidden p-2 rounded-full text-earth-600 hover:bg-earth-200/50"
                data-testid="mobile-menu-toggle"
              >
                {mobileOpen ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileOpen && (
          <div className="lg:hidden border-t border-earth-200/60 bg-earth-50/95 backdrop-blur-xl" data-testid="mobile-nav">
            <div className="px-4 py-3 space-y-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = location.pathname === item.to;
                return (
                  <Link key={item.to} to={item.to} onClick={() => setMobileOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium transition-all duration-300 ${
                      active ? "bg-terracotta-light text-terracotta" : "text-earth-600 hover:bg-earth-200/50"
                    }`}>
                    <Icon size={18} /><span>{item.label}</span>
                  </Link>
                );
              })}
              {user ? (
                <button onClick={() => { logout(); setMobileOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-earth-600 hover:bg-earth-200/50 w-full text-left">
                  <LogOut size={18} /><span>Sign out ({user.name})</span>
                </button>
              ) : (
                <button onClick={() => { promptAuth("Create a free account"); setMobileOpen(false); }}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-medium text-earth-600 hover:bg-earth-200/50 w-full text-left">
                  <User size={18} /><span>Account</span>
                </button>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="min-h-[calc(100vh-4rem)]">{children}</main>

      {/* Quick Exit Button */}
      <button onClick={handleQuickExit} data-testid="quick-exit-button"
        className="fixed bottom-6 right-6 z-50 bg-earth-800 text-earth-50 rounded-full px-5 py-3 font-medium text-sm shadow-xl hover:bg-earth-900 transition-colors duration-300 flex items-center gap-2"
        title="Quick exit - redirects to Google">
        <LogOut size={16} /><span className="hidden sm:inline">Quick Exit</span>
      </button>

      {/* Footer with Disclaimer */}
      <footer className="bg-earth-100 border-t border-earth-200/60 py-8 px-4" data-testid="footer">
        <div className="max-w-7xl mx-auto text-center">
          <img src="/sovha-logo.png" alt="Sovha" className="h-12 mx-auto mb-2" />
          <p className="text-earth-500 text-sm">Your big sister — Always here, always warm.</p>
          <p className="text-earth-400 text-xs mt-4 mb-6">sovha.in</p>
          <div className="max-w-3xl mx-auto border-t border-earth-200/60 pt-6" data-testid="disclaimer">
            <p className="text-earth-400 text-[11px] leading-relaxed">
              Sovha is a peer-support and emotional wellness companion, not a mental health service. The guidance provided is for informational and supportive purposes only and does not constitute medical, psychological, or therapeutic advice. If you are experiencing a crisis, please reach out to a qualified professional or a crisis helpline. By using Sovha, you acknowledge that the app is not a substitute for professional care and that your use is at your own discretion.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
